import { err, ok } from 'neverthrow';
import type {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { toast } from '@cotera/client/app/components/ui';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { WorkflowEditorToolbarActionBase } from './workflow-editor-toolbar-action.base';
import {
  targetedWebhookTriggerNodeModel,
  targetWorkflowIdFromContext,
  TEST_WEBHOOK_ACTIVE_TITLE,
  workflowModelFromContext,
} from './workflow-toolbar-action.helpers';
import { shouldUseDraftTestRun } from './workflow-draft-test-run';

export class WorkflowToolbarTestWebhookAction extends WorkflowEditorToolbarActionBase<
  { t: string; message?: string }[]
> {
  title = 'Test webhook';
  override description = TEST_WEBHOOK_ACTIVE_TITLE;
  group = 'Workflow';
  icon = 'play';

  constructor(
    private readonly workflowAccess: {
      getWorkflow: () => TenantedClient['resource']['workflow'];
    },
    private readonly onTestStarted?: () => void
  ) {
    super();
  }

  override applicable(context: ApplicableContext): boolean {
    const webhook = targetedWebhookTriggerNodeModel(context);
    if (webhook === null) {
      return false;
    }

    return true;
  }

  override async execute(
    _params: Record<string, never>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const webhook = targetedWebhookTriggerNodeModel(context);
    if (webhook === null) {
      return ok({});
    }

    if (shouldUseDraftTestRun(context)) {
      const model = workflowModelFromContext(context);
      if (model === null) {
        toast.error('Could not start draft test');
        return err([{ t: 'error', message: 'Workflow model not in scope' }]);
      }
      const draftResult = await model.startDraftTestRun();
      if (draftResult.success === false) {
        toast.error(draftResult.message);
        return err([{ t: 'error', message: draftResult.message }]);
      }
      toast.success('Draft test started — check the Runs panel');
      this.onTestStarted?.();
      return ok({});
    }

    const workflowId = targetWorkflowIdFromContext(context);
    if (workflowId === null) {
      toast.error('Could not tell which workflow to run');
      return err([{ t: 'error', message: 'Workflow model not in scope' }]);
    }

    const res = await this.workflowAccess.getWorkflow().triggerNow({
      triggerId: webhook.triggerId,
      // This workflow only. A provider delivery fans out to every workflow on
      // the webhook, which is not what testing one graph should do.
      workflowIds: [workflowId],
    });
    if (res.isErr()) {
      toast.error('Could not start webhook test');
      return err([{ t: 'error', message: JSON.stringify(res.error) }]);
    }

    if (res.value.runs.length === 0) {
      toast.success(
        "Test delivered, but this workflow didn't run — check that it's still bound and the event matches the trigger."
      );
      return ok({});
    }

    toast.success('Test webhook delivered — check the Runs panel');
    this.onTestStarted?.();
    return ok({});
  }
}
